/**
 * Thwomp — a SOLID crusher that drops on the player (Brief 012 Phase 2).
 *
 * Reuses the Brief 009 solid spine: while resting it is a standable solid (the player
 * can stand on top / be blocked by it like a block). A deterministic state machine:
 *
 *   IDLE   — anchored up high, SOLID. When the player enters its column BELOW within
 *            `props.triggerRange` tiles → FALLING.
 *   FALLING— drops fast (NON-solid, so it passes over the player and the overlap pass
 *            cleanly registers the crush). Stops at the floor it scanned below itself.
 *            Contact while falling = hurt (or instakill via ctx.killPlayer if props.crush).
 *   REST   — sits on the floor for a short pause, SOLID again (standable).
 *   RISING — returns to the anchor (NON-solid so it never shoves the player), then IDLE.
 *
 * Prereq: the Brief 009 solid-entity spine (solidBody/onStand) — present.
 *
 * Determinism: vertical motion is a fixed-step parametric (speed·dt, like Platform); the
 * rest pause is an integer step counter; the trigger reads only player/grid positions.
 * No Math.sin, no wall-clock, no RNG. ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const FALL_SPEED = 920;  // px/s — a fast, heavy drop
const RISE_SPEED = 170;  // px/s — slow grind back up
const REST_STEPS = 28;   // ~0.47s pause at the bottom

type ThwompState = 'idle' | 'falling' | 'rest' | 'rising';

function numProp(props: Record<string, unknown> | undefined, key: string, fallback: number): number {
  const v = props?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export class Thwomp implements Entity {
  readonly type = EntityType.THWOMP;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private state: ThwompState = 'idle';
  private readonly anchorY: number;
  /** Lowest body.y the crusher can reach (top of the floor below − body height).
   *  Computed lazily on the first update once ctx.grid is available; Infinity = unset. */
  private floorY = Infinity;
  private restCounter = 0;
  private readonly triggerRange: number; // tiles
  private readonly crush: boolean;       // true → instakill on a falling hit

  /**
   * @param cx    placed-cell center-x, @param feetY placed-cell bottom.
   * @param props `triggerRange` (tiles below to watch, def 5), `crush` (def false →
   *              falling hit costs a heart; true → instakill/respawn).
   * @param ts    tile size (px) — the crusher fills one cell.
   */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    this.anchorY = this.body.y;
    this.triggerRange = Math.max(1, numProp(props, 'triggerRange', 5));
    this.crush = props?.crush === true;
  }

  /** Solid (standable / blocking) only while resting — never while in motion. */
  solidBody(): AABB | null {
    return this.state === 'idle' || this.state === 'rest' ? this.body : null;
  }

  update(dt: number, ctx: EntityContext): void {
    if (this.floorY === Infinity) this.floorY = this.computeFloorY(ctx);
    switch (this.state) {
      case 'idle':
        if (this.triggered(ctx)) this.state = 'falling';
        break;
      case 'falling':
        this.body.y += FALL_SPEED * dt;
        if (this.body.y >= this.floorY) {
          this.body.y = this.floorY;
          this.state = 'rest';
          this.restCounter = REST_STEPS;
        }
        break;
      case 'rest':
        if (--this.restCounter <= 0) this.state = 'rising';
        break;
      case 'rising':
        this.body.y -= RISE_SPEED * dt;
        if (this.body.y <= this.anchorY) {
          this.body.y = this.anchorY;
          this.state = 'idle';
        }
        break;
    }
  }

  /** Player is horizontally under the crusher AND within triggerRange tiles below it. */
  private triggered(ctx: EntityContext): boolean {
    const p = ctx.player.body;
    const overlapX = p.x < this.body.x + this.body.w && p.x + p.w > this.body.x;
    if (!overlapX) return false;
    const gap = p.y - (this.body.y + this.body.h); // player top − crusher bottom
    return gap >= 0 && gap <= this.triggerRange * ctx.grid.ts;
  }

  /** Scan straight down from the crusher for the first SOLID tile; rest on its top.
   *  No solid below (open pit) → the world floor. */
  private computeFloorY(ctx: EntityContext): number {
    const ts = ctx.grid.ts;
    const col = Math.floor((this.body.x + this.body.w / 2) / ts);
    let row = Math.floor((this.body.y + this.body.h) / ts); // first row below the crusher
    while (row < ctx.grid.rows && !ctx.grid.isSolid(col, row)) row++;
    return row * ts - this.body.h; // body bottom seats on the tile top
  }

  onPlayerOverlap(ctx: EntityContext): void {
    // Damaging ONLY while crashing down; idle/rest/rising are a harmless (solid) block.
    if (this.state !== 'falling') return;
    if (this.crush) ctx.killPlayer();
    else ctx.hurtPlayer();
  }

  spriteKey(): string { return 'thwomp'; }
  visible(): boolean { return true; }
}
